import React, { useId } from "react";

import { typographyStyles } from "@newsela/angelou";

import ExportablesTemplateHeaderSection from "../components/ExportablesTemplateHeaderSection";
import SectionInput from "../components/SectionInput";
import { TemplateProps } from "../registry";
import {
  containerStyles,
  formGridStyles,
  paragraphCss,
  quadrantStyles,
  quadrantTextareaStyles,
  wordInputStyles,
  wordWrapperStyles,
} from "./FrayerModelOrganizer.styles";

export interface FrayerData {
  characteristics?: string;
  word_candidates?: Array<{ word?: string; definition?: string }>;
  definition?: string;
  examples?: string;
  nonExamples?: string;
  word?: string;
  sectionOneTitle?: string;
  sectionTwoTitle?: string;
  sectionThreeTitle?: string;
  sectionFourTitle?: string;
}

// Type guard to check if formData is FrayerData
const isFrayerData = (data: any): data is FrayerData => {
  return (
    data &&
    typeof data === "object" &&
    ("characteristics" in data ||
      "word_candidates" in data ||
      "definition" in data ||
      "examples" in data ||
      "nonExamples" in data ||
      "sectionOneTitle" in data ||
      "sectionTwoTitle" in data ||
      "sectionThreeTitle" in data ||
      "sectionFourTitle" in data ||
      "word" in data)
  );
};

const FrayerModelOrganizer = ({
  formData,
  onChange,
  title = "Building word knowledge with Frayer model",
  isStudent = false,
}: TemplateProps<FrayerData>) => {
  const definitionId = useId();
  const characteristicsId = useId();
  const examplesId = useId();
  const nonExamplesId = useId();

  // Ensure formData is treated as FrayerData
  const frayerData = isFrayerData(formData) ? formData : ({} as FrayerData);
  const wordCandidates = frayerData?.word_candidates || [];
  const selectedWord = wordCandidates?.[0] || {};

  const handleChange = (field: keyof FrayerData, value: string) => {
    if (onChange) {
      onChange({ ...(frayerData || {}), [field]: value });
    }
  };

  return (
    <div css={containerStyles}>
      <ExportablesTemplateHeaderSection templateTitle={title} />
      <div css={formGridStyles}>
        {/* word in the middle */}
        <div css={wordWrapperStyles}>
          {isStudent ? (
            <p css={paragraphCss}>
              {frayerData?.word ?? selectedWord.word ?? ""}
            </p>
          ) : (
            <input
              data-qa-selector="exportables-frayer-word-input"
              type="text"
              css={wordInputStyles}
              value={frayerData?.word ?? selectedWord.word ?? ""}
              onChange={(e) => handleChange("word", e.target.value)}
              placeholder="Enter word here..."
              aria-label="word"
              maxLength={20}
            />
          )}
        </div>
        <div css={quadrantStyles} className="quadrant-top-left">
          <SectionInput
            isStudent={isStudent}
            styleOverrides={typographyStyles.heading4Obj}
            align="left"
            placeholder="Definition"
            value={frayerData?.sectionOneTitle || "Definition"}
            onChange={(e) => handleChange("sectionOneTitle", e.target.value)}
          />
          <textarea
            data-qa-selector="exportables-frayer-definition-input"
            id={definitionId}
            aria-label="Frayer Model Definition Textarea"
            css={quadrantTextareaStyles}
            value={frayerData?.definition ?? selectedWord.definition ?? ""}
            onChange={(e) => handleChange("definition", e.target.value)}
            placeholder="Enter definition here..."
            maxLength={100}
          />
        </div>
        <div css={quadrantStyles} className="quadrant-top-right">
          <SectionInput
            isStudent={isStudent}
            styleOverrides={typographyStyles.heading4Obj}
            align="right"
            placeholder="Characteristics"
            value={frayerData?.sectionTwoTitle || "Characteristics"}
            onChange={(e) => handleChange("sectionTwoTitle", e.target.value)}
          />
          <textarea
            data-qa-selector="exportables-frayer-characteristics-input"
            id={characteristicsId}
            aria-label="Frayer Model Characteristics Textarea"
            css={quadrantTextareaStyles}
            value={frayerData?.characteristics || ""}
            onChange={(e) => handleChange("characteristics", e.target.value)}
            placeholder="Enter characteristics here..."
            maxLength={100}
          />
        </div>
        <div css={quadrantStyles} className="quadrant-bottom-left">
          <SectionInput
            isStudent={isStudent}
            styleOverrides={typographyStyles.heading4Obj}
            align="left"
            placeholder="Examples"
            value={frayerData?.sectionThreeTitle || "Examples"}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              handleChange("sectionThreeTitle", e.target.value)
            }
          />
          <textarea
            data-qa-selector="exportables-frayer-examples-input"
            id={examplesId}
            aria-label="Frayer Model Examples Textarea"
            css={quadrantTextareaStyles}
            value={frayerData?.examples || ""}
            onChange={(e) => handleChange("examples", e.target.value)}
            placeholder="Enter examples here..."
            maxLength={100}
          />
        </div>
        <div css={quadrantStyles} className="quadrant-bottom-right">
          <SectionInput
            isStudent={isStudent}
            styleOverrides={typographyStyles.heading4Obj}
            align="right"
            placeholder="Non-examples"
            value={frayerData?.sectionFourTitle || "Non-examples"}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              handleChange("sectionFourTitle", e.target.value)
            }
          />
          <textarea
            data-qa-selector="exportables-frayer-non-examples-input"
            id={nonExamplesId}
            aria-label="Frayer Model Non-examples Textarea"
            css={quadrantTextareaStyles}
            value={frayerData?.nonExamples || ""}
            onChange={(e) => handleChange("nonExamples", e.target.value)}
            placeholder="Enter non-examples here..."
            maxLength={100}
          />
        </div>
      </div>
    </div>
  );
};

export default FrayerModelOrganizer;
